import { ApiError, type MissingStockOrder, type MissingStockPage } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { formatDateTime } from "@khazzin/i18n/dates";
import { EmptyState } from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import { Button, Drawer, Field, Input } from "@khazzin/ui/primitives";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import type { Level } from "../../app/shell/sections";

/**
 * The Missing Stock tab (EP10-S04, DA-10-03): the store's orders waiting on
 * stock, their short lines highlighted, and at `write` an Edit lines drawer
 * that cuts a short line to what is free so the order can release.
 */

export function missingStockKey(storeId: string) {
	return ["portal", storeId, "missing-stock"] as const;
}

export function MissingStockTab({
	storeId,
	level,
	timeZone,
}: {
	storeId: string;
	level: Level;
	timeZone: string;
}) {
	const { t, locale } = useLocale();
	const api = useApi();
	const [editing, setEditing] = useState<MissingStockOrder | null>(null);
	const [notice, setNotice] = useState<string | null>(null);

	const page = useCursorPagination<
		MissingStockOrder,
		MissingStockPage & { nextCursor: string | null }
	>(
		async (cursor, size, signal) => {
			const answer = await api.listMissingStockOrders({
				limit: size,
				...(cursor === null ? {} : { cursor }),
				signal,
			});
			return { ...answer, nextCursor: answer.nextCursor ?? null };
		},
		{ queryKey: missingStockKey(storeId), limit: 50, maxLimit: 200, enabled: storeId !== "" },
	);

	return (
		<div className="flex flex-col gap-3">
			{notice !== null && (
				<p role="status" className="text-[12.5px] text-fg">
					{notice}
				</p>
			)}
			<DataTable<MissingStockOrder>
				screenKey="portal.orders.missingStock"
				caption={t("portal.orders.tabs.missing_stock")}
				t={t}
				columns={missingColumns(t, locale, timeZone)}
				rowKey={(row) => row.orderId}
				page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				collapse="cards"
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				{...(level === "write"
					? {
							rowActions: (row: MissingStockOrder) => (
								<Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
									{t("portal.orders.missing.editLines")}
								</Button>
							),
						}
					: {})}
				empty={
					<EmptyState
						title={t("portal.orders.missing.empty.title")}
						body={t("portal.orders.missing.empty.body")}
					/>
				}
			/>
			{editing !== null && (
				<EditLinesDrawer
					key={editing.orderId}
					order={editing}
					storeId={storeId}
					onClose={() => setEditing(null)}
					onSaved={(message) => {
						setEditing(null);
						setNotice(message);
					}}
				/>
			)}
		</div>
	);
}

function missingColumns(
	t: Translate,
	locale: string,
	timeZone: string,
): Column<MissingStockOrder>[] {
	return [
		{
			id: "orderNo",
			headerKey: "portal.orders.columns.orderNo",
			mono: true,
			card: "wide",
			cell: (row) => (
				<Link
					to={`/orders/${encodeURIComponent(row.orderId)}`}
					className="font-semibold text-brand-ink underline-offset-2 hover:underline"
				>
					{row.orderNo}
				</Link>
			),
		},
		{
			id: "customer",
			headerKey: "portal.orders.columns.customer",
			cell: (row) => row.customerName,
		},
		{
			id: "short",
			headerKey: "portal.orders.missing.columns.short",
			card: "wide",
			cell: (row) => (
				<ul className="flex flex-col gap-1">
					{row.lines.map((line) => (
						<li key={line.orderLineId} className="flex flex-wrap items-center gap-1.5">
							<span className="font-mono text-[12.5px]">{line.sku}</span>
							<StatusChip tone="danger" size="sm">
								{t("portal.orders.missing.short", {
									short: line.qtyShort,
									ordered: line.qtyOrdered,
								})}
							</StatusChip>
							{line.available !== null && line.available !== undefined && (
								<span className="text-[12px] text-fg-muted">
									{t("portal.orders.missing.available", { count: line.available })}
								</span>
							)}
						</li>
					))}
				</ul>
			),
		},
		{
			id: "waiting",
			headerKey: "portal.orders.missing.columns.waiting",
			hideable: true,
			cell: (row) =>
				formatDateTime(row.awaitingStockSince ?? row.createdAt, {
					locale: locale as "en" | "ar",
					timeZone,
				}),
		},
	];
}

/** Each short line's new quantity, and the cash to collect if it changes. */
function EditLinesDrawer({
	order,
	storeId,
	onClose,
	onSaved,
}: {
	order: MissingStockOrder;
	storeId: string;
	onClose: () => void;
	onSaved: (message: string) => void;
}) {
	const { t } = useLocale();
	const api = useApi();
	const queries = useQueryClient();
	const [qty, setQty] = useState<Record<string, string>>(() =>
		Object.fromEntries(order.lines.map((line) => [line.orderLineId, String(line.qtyOrdered)])),
	);
	// The currency's own minor unit: cents for dollars, none for pounds.
	const scale =
		10 **
		(new Intl.NumberFormat("en", { style: "currency", currency: order.currency }).resolvedOptions()
			.maximumFractionDigits ?? 2);
	const [cod, setCod] = useState(String(order.codAmountMinor / scale));
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<string | null>(null);

	const changed = order.lines
		.filter((line) => Number(qty[line.orderLineId]) !== line.qtyOrdered)
		.map((line) => ({ orderLineId: line.orderLineId, qty: Number(qty[line.orderLineId]) }));
	const valid = order.lines.every((line) => {
		const n = Number(qty[line.orderLineId]);
		return Number.isInteger(n) && n >= 1 && n <= line.qtyOrdered;
	});
	const codMinor = Math.round(Number(cod) * scale);
	// ADJ-M03-4: why Save is off, said rather than left to guess. The edit
	// exists to cut a short line; the cash is corrected with it, not alone
	// (the server needs at least one line).
	const blocked = !valid
		? "portal.orders.missing.blockedQty"
		: changed.length === 0
			? "portal.orders.missing.blockedNoLine"
			: null;

	async function save() {
		setBusy(true);
		setFailure(null);
		try {
			const result = await api.editPortalShortLines(
				order.orderId,
				{
					lines: changed,
					...(codMinor !== order.codAmountMinor && Number.isFinite(codMinor) && codMinor >= 0
						? { codAmountMinor: codMinor }
						: {}),
				},
				order.version,
			);
			// ADJ-M03-4: what the edit moves — this list, the orders, the
			// dashboard and the stock it reserved — not every read of the store.
			await Promise.all(
				[
					missingStockKey(storeId),
					["portal", storeId, "orders"],
					["portal", storeId, "dashboard"],
					["portal", storeId, "inventory"],
				].map((queryKey) => queries.invalidateQueries({ queryKey })),
			);
			onSaved(
				t(
					result.awaitingStock
						? "portal.orders.missing.stillWaiting"
						: "portal.orders.missing.released",
					{
						orderNo: result.orderNo,
					},
				),
			);
		} catch (error) {
			setFailure(
				error instanceof ApiError && error.code === "line_not_short"
					? t("portal.orders.missing.notShort")
					: error instanceof ApiError && error.status === 409
						? t("portal.orders.missing.stale")
						: t("portal.orders.missing.failed"),
			);
		} finally {
			setBusy(false);
		}
	}

	return (
		<Drawer
			open
			onOpenChange={(open) => !open && onClose()}
			title={t("portal.orders.missing.drawerTitle", { orderNo: order.orderNo })}
			description={t("portal.orders.missing.drawerBody")}
			closeLabel={t("ui.actions.close")}
			footer={
				<>
					<Button variant="ghost" onClick={onClose}>
						{t("ui.actions.cancel")}
					</Button>
					<Button
						variant="primary"
						loading={busy}
						disabled={blocked !== null}
						onClick={() => void save()}
					>
						{t("ui.actions.save")}
					</Button>
				</>
			}
		>
			<div className="flex flex-col gap-4">
				{order.lines.map((line) => (
					<Field
						key={line.orderLineId}
						label={<span className="font-mono">{line.sku}</span>}
						hint={t("portal.orders.missing.lineHint", {
							ordered: line.qtyOrdered,
							available: line.available ?? "—",
						})}
					>
						{(control) => (
							<Input
								{...control}
								type="number"
								inputMode="numeric"
								min={1}
								max={line.qtyOrdered}
								value={qty[line.orderLineId] ?? ""}
								onChange={(event) => setQty({ ...qty, [line.orderLineId]: event.target.value })}
							/>
						)}
					</Field>
				))}
				<Field label={t("portal.orders.missing.cod")}>
					{(control) => (
						<Input
							{...control}
							type="number"
							inputMode="decimal"
							min={0}
							step="any"
							value={cod}
							onChange={(event) => setCod(event.target.value)}
						/>
					)}
				</Field>
				{blocked !== null && <p className="text-[12.5px] text-fg-muted">{t(blocked)}</p>}
				{failure !== null && (
					<p role="alert" className="text-[12.5px] text-danger-fg">
						{failure}
					</p>
				)}
			</div>
		</Drawer>
	);
}
